import { useState, useMemo, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles, X, CheckCircle2, AlertTriangle, Info } from 'lucide-react';

const C = {
  primary: '#1E4E8C', primaryText: 'var(--tone-navy-text)', accent: '#F26B1F',
  slate: 'var(--text-primary)', slateMuted: 'var(--text-muted)',
  border: 'rgba(30,78,140,0.14)', surface: 'var(--card-bg)', surfaceAlt: 'var(--card-bg-alt)',
};

/**
 * Human-in-loop review dialog for Cognara bulk forecast suggestions.
 * Works for BOTH per-MCI (rows = 1) and register-wide bulk apply.
 * User can tick/untick rows, edit dates, then Apply → POST bulk-apply.
 */
export default function BulkForecastReview({
  projectId, rows,  // [{mciLineNumber, materialDescription, vendor, priority, suggestions:[{phase,stageGateId,stageGateName,plannedDate,currentForecast,suggestedForecast,reason,confidence}]}]
  onClose, onApplied, title = 'Cognara Bulk Forecast Review',
}) {
  // Build editable state {mci|phase|gateId: {selected, date, reason}}
  // Include phase because stageGateIds commonly collide between
  // preOrderProgress and postOrderProgress within the same MCI
  // (~28 collisions on proj_001) — without phase, the state map
  // silently drops the pre entry when post overwrites it, so the
  // header count (state.length) drifts below rows total (494 vs 522).
  const initial = useMemo(() => {
    const m = {};
    (rows || []).forEach(r => (r.suggestions || []).forEach(s => {
      m[`${r.mciLineNumber}|${s.phase}|${s.stageGateId}`] = {
        selected: true,
        date: (s.suggestedForecast || '').slice(0, 10),
        reason: s.reason,
      };
    }));
    return m;
  }, [rows]);
  const [state, setState] = useState(initial);
  const [applying, setApplying] = useState(false);
  useEffect(() => { setState(initial); }, [initial]);

  // ── In-modal filters ─────────────────────────────────────────────
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('ALL');
  const [prioFilter, setPrioFilter] = useState('ALL');
  const [confFilter, setConfFilter] = useState('ALL');
  const [poLineFilter, setPoLineFilter] = useState('');

  const filteredRows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const pol = poLineFilter.trim().toLowerCase();
    return (rows || []).map(r => {
      if (catFilter !== 'ALL' && (r.materialCategory || '').toUpperCase() !== catFilter) return null;
      if (prioFilter !== 'ALL' && (r.priority || '').toUpperCase() !== prioFilter) return null;
      if (pol && !(r.poLineItem || '').toLowerCase().includes(pol)) return null;
      const suggestions = (r.suggestions || []).filter(s =>
        (confFilter === 'ALL' || (s.confidence || 'medium') === confFilter));
      if (!suggestions.length) return null;
      if (qq) {
        const hay = `${r.mciLineNumber} ${r.materialDescription || ''} ${r.vendor || ''} ${r.discipline || ''} ${r.poNumber || ''} ${r.poLineItem || ''}`.toLowerCase();
        if (!hay.includes(qq)) return null;
      }
      return { ...r, suggestions };
    }).filter(Boolean);
  }, [rows, q, catFilter, prioFilter, confFilter, poLineFilter]);

  // Which state keys are currently VISIBLE under the filters (drives header + apply)?
  const visibleKeys = useMemo(() => {
    const keys = new Set();
    filteredRows.forEach(r => r.suggestions.forEach(s =>
      keys.add(`${r.mciLineNumber}|${s.phase}|${s.stageGateId}`)));
    return keys;
  }, [filteredRows]);

  const stats = useMemo(() => {
    const total = visibleKeys.size;
    let selected = 0;
    visibleKeys.forEach(k => { if (state[k]?.selected) selected++; });
    return { total, selected };
  }, [state, visibleKeys]);

  const toggleAll = (on) => {
    const next = { ...state };
    visibleKeys.forEach(k => { next[k] = { ...(next[k] || {}), selected: on }; });
    setState(next);
  };

  const apply = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const items = [];
    for (const r of filteredRows) {
      for (const s of r.suggestions || []) {
        const key = `${r.mciLineNumber}|${s.phase}|${s.stageGateId}`;
        const v = state[key];
        if (!v?.selected) continue;
        if (!v.date || v.date <= today) {
          toast.error(`${r.mciLineNumber} · ${s.stageGateName}: date must be in the future`);
          return;
        }
        items.push({
          mciLineNumber: r.mciLineNumber,
          stageGateId: s.stageGateId,
          phase: s.phase,
          forecastDate: `${v.date}T00:00:00Z`,
        });
      }
    }
    if (items.length === 0) return toast.info('Nothing selected');
    setApplying(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/mci/control-tower/gate-forecast/bulk-apply`,
        { items });
      const parts = [`Applied ${data.applied}`];
      if (data.nudged) parts.push(`Nudged ${data.nudged} past-date forecasts to tomorrow`);
      if (data.alreadyDone) parts.push(`${data.alreadyDone} already DONE (no-op)`);
      if (data.skipped) parts.push(`Skipped ${data.skipped}`);
      toast.success(parts.join(' · '), { duration: 6000 });
      window.dispatchEvent(new CustomEvent('mci-register-updated'));
      onApplied?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk apply failed');
    }
    setApplying(false);
  };

  const setKey = (k, patch) => setState(s => ({ ...s, [k]: { ...s[k], ...patch } }));
  const confBg = { high: 'var(--tone-blue-bg)', medium: 'var(--tone-amber-bg)', low: 'var(--tone-red-bg)' };
  const confColor = { high: 'var(--tone-blue-text)', medium: 'var(--tone-amber-text)', low: 'var(--tone-red-text)' };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onClose}
      data-testid="bulk-forecast-review">
      <div className="w-full max-w-5xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl"
        onClick={e => e.stopPropagation()}
        style={{ background: C.surface, border: `1px solid ${C.border}` }}>
        {/* Header */}
        <div className="px-5 py-3 flex items-center justify-between border-b" style={{ borderColor: C.border }}>
          <div>
            <div className="flex items-center gap-2 text-base font-bold" style={{ color: C.primaryText }}>
              <Sparkles className="w-4 h-4" style={{ color: C.accent }} /> {title}
            </div>
            <div className="text-[10px]" style={{ color: C.slateMuted }}>
              {rows.length} MCI(s) · {stats.selected} of {stats.total} gate suggestions selected · Human-in-loop
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" style={{ color: C.slateMuted }} /></button>
        </div>

        {/* Toolbar */}
        <div className="px-5 py-2 flex flex-wrap items-center gap-2 border-b" style={{ borderColor: C.border, background: C.surfaceAlt }}>
          <Button size="sm" variant="outline" onClick={() => toggleAll(true)} data-testid="select-all-btn">Select all</Button>
          <Button size="sm" variant="outline" onClick={() => toggleAll(false)} data-testid="clear-all-btn">Clear all</Button>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search MCI / vendor / description…"
            className="px-2 py-1 rounded-md text-[11px] border w-56"
            style={{ background: C.surface, borderColor: C.border, color: C.slate }}
            data-testid="bfr-search" />
          <input value={poLineFilter} onChange={e => setPoLineFilter(e.target.value)} placeholder="PO Line (L01)"
            className="px-2 py-1 rounded-md text-[11px] border w-24"
            style={{ background: C.surface, borderColor: C.border, color: C.slate }}
            data-testid="bfr-poline" />
          <div className="flex rounded-md overflow-hidden border" style={{ borderColor: C.border }}>
            {['ALL','BULK','TAGGED'].map(c => (
              <button key={c} onClick={() => setCatFilter(c)}
                className="px-2 py-1 text-[10px] font-semibold"
                style={{ background: catFilter === c ? '#1E4E8C' : C.surface, color: catFilter === c ? '#ffffff' : C.slateMuted }}
                data-testid={`bfr-cat-${c.toLowerCase()}`}>{c}</button>
            ))}
          </div>
          <div className="flex rounded-md overflow-hidden border" style={{ borderColor: C.border }}>
            {['ALL','CRITICAL','HIGH','NORMAL','LOW'].map(p => (
              <button key={p} onClick={() => setPrioFilter(p)}
                className="px-2 py-1 text-[10px] font-semibold"
                style={{ background: prioFilter === p ? '#1E4E8C' : C.surface, color: prioFilter === p ? '#ffffff' : C.slateMuted }}
                data-testid={`bfr-prio-${p.toLowerCase()}`}>{p}</button>
            ))}
          </div>
          <div className="flex rounded-md overflow-hidden border" style={{ borderColor: C.border }}>
            {['ALL','high','medium','low'].map(cf => (
              <button key={cf} onClick={() => setConfFilter(cf)}
                className="px-2 py-1 text-[10px] font-semibold"
                style={{ background: confFilter === cf ? '#1E4E8C' : C.surface, color: confFilter === cf ? '#ffffff' : C.slateMuted }}
                data-testid={`bfr-conf-${cf.toLowerCase()}`}>{cf === 'ALL' ? 'ALL' : cf.toUpperCase()}</button>
            ))}
          </div>
          <div className="ml-auto text-[10px]" style={{ color: C.slateMuted }}>
            Confidence ranges from vendor-specific slip history to category defaults.
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-3">
          {filteredRows.map(r => (
            <div key={r.mciLineNumber} className="mb-3 rounded-lg border overflow-hidden" style={{ borderColor: C.border }}
              data-testid={`bulk-row-${r.mciLineNumber}`}>
              <div className="px-3 py-2 flex items-center justify-between" style={{ background: C.surfaceAlt }}>
                <div className="text-[11px]">
                  <span className="font-mono font-bold" style={{ color: C.primaryText }}>{r.mciLineNumber}</span>
                  <span className="mx-2" style={{ color: C.slateMuted }}>·</span>
                  <span style={{ color: C.slate }}>{r.materialDescription}</span>
                  <span className="mx-2" style={{ color: C.slateMuted }}>·</span>
                  <span style={{ color: C.slateMuted }}>{r.vendor || '—'}</span>
                </div>
                <span className="text-[9px] px-1.5 py-0.5 rounded font-bold"
                  style={{ background: r.priority === 'CRITICAL' ? 'var(--tone-red-bg)' : 'var(--tone-slate-bg)',
                            color: r.priority === 'CRITICAL' ? 'var(--tone-red-text)' : C.slate }}>
                  {r.priority}
                </span>
              </div>
              <table className="w-full text-[11px]">
                <thead>
                  <tr style={{ background: C.surface }}>
                    <th className="p-1.5 text-left" style={{ color: C.slateMuted, width: 30 }}></th>
                    <th className="p-1.5 text-left" style={{ color: C.slateMuted }}>Gate</th>
                    <th className="p-1.5 text-left" style={{ color: C.slateMuted }}>Phase</th>
                    <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>Planned</th>
                    <th className="p-1.5 text-right" style={{ color: C.slateMuted }}>Current FC</th>
                    <th className="p-1.5 text-left" style={{ color: C.slateMuted }}>Cognara Forecast</th>
                    <th className="p-1.5 text-left" style={{ color: C.slateMuted }}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {(r.suggestions || []).map(s => {
                    const k = `${r.mciLineNumber}|${s.phase}|${s.stageGateId}`;
                    const v = state[k] || {};
                    return (
                      <tr key={`${r.mciLineNumber}-${s.phase}-${s.stageGateId}`} style={{ borderTop: `1px solid ${C.border}` }}
                        data-testid={`bulk-sugg-${r.mciLineNumber}-${s.phase}-${s.stageGateId}`}>
                        <td className="p-1.5 text-center">
                          <input type="checkbox" checked={!!v.selected}
                            onChange={e => setKey(k, { selected: e.target.checked })}
                            data-testid={`chk-${r.mciLineNumber}-${s.stageGateId}`} />
                        </td>
                        <td className="p-1.5" style={{ color: C.slate }}>{s.stageGateName}</td>
                        <td className="p-1.5 text-[9px] uppercase" style={{ color: C.slateMuted }}>{s.phase}</td>
                        <td className="p-1.5 text-right font-mono text-[10px]" style={{ color: C.slateMuted }}>{s.plannedDate?.slice(0, 10) || '—'}</td>
                        <td className="p-1.5 text-right font-mono text-[10px]" style={{ color: C.slateMuted }}>{s.currentForecast?.slice(0, 10) || '—'}</td>
                        <td className="p-1.5">
                          <input type="date" value={v.date || ''} onChange={e => setKey(k, { date: e.target.value })}
                            className="px-1 py-0.5 rounded border text-[10px]"
                            style={{ borderColor: C.border, background: C.surfaceAlt }}
                            data-testid={`date-${r.mciLineNumber}-${s.stageGateId}`} />
                          <span className="ml-2 text-[9px] px-1 rounded"
                            style={{ background: confBg[s.confidence] || confBg.medium,
                                      color: confColor[s.confidence] || confColor.medium }}>
                            {s.confidence || 'medium'}
                          </span>
                        </td>
                        <td className="p-1.5 text-[10px]" style={{ color: C.slateMuted }}>
                          <div className="flex items-start gap-1">
                            <span>{s.reason}</span>
                            {s.slipDetail && (
                              <span className="relative group inline-flex" data-testid={`slip-info-${r.mciLineNumber}-${s.stageGateId}`}>
                                <Info className="w-3 h-3 mt-[1px] flex-shrink-0 cursor-help" style={{ color: C.slateMuted }} />
                                <span className="pointer-events-none absolute z-50 right-0 top-4 hidden group-hover:block w-64 p-2 rounded-md shadow-xl text-[10px]"
                                  style={{ background: 'var(--text-primary)', color: 'var(--border-color)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                  <div className="font-bold mb-1" style={{ color: '#F26B1F' }}>Slip Breakdown</div>
                                  <div>This package: <b>{s.slipDetail.thisPackage?.n ?? 0}</b> gate(s)
                                    {s.slipDetail.thisPackage?.avg != null && <> · <b>{s.slipDetail.thisPackage.avg}d</b> avg</>}
                                  </div>
                                  <div>Vendor-wide: <b>{s.slipDetail.vendorWide?.n ?? 0}</b> gate(s) across
                                    <b> {s.slipDetail.vendorWide?.mciCount ?? 0}</b> MCI(s)
                                    {s.slipDetail.vendorWide?.avg != null && <> · <b>{s.slipDetail.vendorWide.avg}d</b> avg</>}
                                  </div>
                                  <div>Category default: <b>{s.slipDetail.categoryDefault}d</b></div>
                                  <div className="mt-1 pt-1 border-t border-slate-700">
                                    Applied: <b>{s.slipDetail.applied}d</b> · source <b>{s.slipDetail.source}</b>
                                  </div>
                                </span>
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
          {(!filteredRows || filteredRows.length === 0) && (
            <div className="py-10 text-center text-xs flex items-center justify-center gap-2" style={{ color: C.slateMuted }}>
              <AlertTriangle className="w-4 h-4" />
              {(!rows || rows.length === 0)
                ? 'No open gates to forecast — all planned dates are already actualised or blank.'
                : 'No MCIs match the current filter.'}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex items-center justify-between" style={{ borderColor: C.border, background: C.surfaceAlt }}>
          <div className="text-[10px]" style={{ color: C.slateMuted }}>
            <CheckCircle2 className="w-3 h-3 inline mr-0.5" style={{ color: '#16A34A' }} />
            Server rejects forecast dates ≤ today and gates already DONE — safe to bulk-apply.
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={apply} disabled={applying || stats.selected === 0}
              className="text-white" style={{ background: C.primaryText }}
              data-testid="apply-bulk-btn">
              {applying && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
              Apply {stats.selected} suggestion{stats.selected === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
